import React from 'react';
import {
  Background,
  Controls,
  Panel,
  ReactFlow,
  useNodesState,
  type Edge,
  type FitViewOptions,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { STORYBOARD_COMPOSE } from '@/nova/theme/tokens';
import type { VideoAspectRatio } from '@/store/slices/creativeJobs';

import { moveBeat, removeBeat, updateBeat, type StoryboardDraft } from '../../storyboard';
import { CanvasActionsContext, PromoPanelContext, type CanvasActions } from './context';
import { deriveGraph, type StoryboardNode } from './graph';
import { BeatNode } from './nodes/BeatNode';
import { BrandNode } from './nodes/BrandNode';
import { FrameNode } from './nodes/FrameNode';
import { OutputNode } from './nodes/OutputNode';
import { PromoNode } from './nodes/PromoNode';
import { CanvasGlowAngleProperty } from './nodes/nodes.styles';
import { CanvasFrame, FooterPill } from './StoryboardCanvas.styles';

/** MUST be module-level: a per-render `nodeTypes` object remounts every node, dropping input focus
 *  while the owner types. */
const nodeTypes = { frame: FrameNode, beat: BeatNode, brand: BrandNode, output: OutputNode, promo: PromoNode };

export interface StoryboardCanvasProps {
  draft: StoryboardDraft;
  keyArt?: string;
  /** Brand identity shown on the F0 origin card (logo + domain), also baked into the finished ad. */
  brandLogoUrl?: string;
  brandDomain?: string;
  onChange: (next: StoryboardDraft) => void;
  /** Live run: index-aligned generated end-frame image URLs, shown on the frame nodes as they arrive. */
  generatedFrames?: string[];
  /** Live run: the id of the node currently being worked (drives the active/done choreography). */
  stage?: string;
  /** Live run: whether a generation is in flight (nothing lights up unless this is true). */
  running?: boolean;
  /** Index-aligned CLIP urls (`segments[j]` = beat j's clip), surfaced on the nodes' view affordance. */
  segments?: string[];
  /** The 1-based beat a regenerate/resume run starts from: beats at/after it read as `stale`. */
  staleFromBeat?: number;
  /** Compose loader: the storyboard is being written by the LLM. `step:-1` = seeding (every beat renders as
   *  a shimmer skeleton while the model runs); `step>=0` = the 0-based beat whose motion prose is typing in
   *  (earlier beats done, later ones skeletons); `typed` = chars revealed so far. Absent = a resting canvas.
   *  This replaces the old full-canvas "Composing…" overlay with an in-context, per-beat loader. */
  compose?: { step: number; typed: number };
  /** The durations (seconds) the chosen video model accepts — the beat duration dropdown options. */
  videoDurations: number[];
  /** The aspect ratios the chosen video model can produce — the output node's aspect picker options. */
  videoAspects?: VideoAspectRatio[];
  /** On a FAILED run: the 1-based beat it stopped on, and a short reason shown under that beat's card. */
  failedBeat?: number;
  beatError?: string;
  /** Start a regenerate/resume from a beat (1-based): reuses 1..K-1, re-runs K..N. */
  onRegenerateFrom: (beatIndex: number) => void;
  /** Video renders are blocked (a mature primary art style): every beat's Regenerate control is disabled. */
  renderBlocked?: boolean;
  /** Open the beat-preview modal for a beat (1-based). */
  onViewBeat: (index: number) => void;
  /** Immersive editor: full-bleed canvas (no card border/radius) with the combined toolbar/footer pill over it. */
  immersed?: boolean;
  /** When true the combined bar renders as a floating React Flow Panel over the board (desktop immersive);
   *  otherwise the composer stacks the toolbar/footer in its own column and passes neither. */
  floating?: boolean;
  /** The combined controls + generate + status bar, mounted in the bottom floating pill when `floating`. */
  footer?: React.ReactNode;
  /** The promotion picker + prompt + regenerate controls (the same element the composer stacks on mobile),
   *  rendered INSIDE the on-canvas `promo` node via PromoPanelContext when `floating`. */
  promoPanel?: React.ReactNode;
  /** The finished ad shown INSIDE the output ("Finished ad") node: the rendered version history (newest
   *  first), which one is selected, the poster + download name. Absent until at least one render finishes. */
  finishedAd?: {
    versions?: { key: string; url?: string; storyboard?: unknown; createdAt: number }[];
    selectedVersionKey?: string;
    poster?: string;
    downloadName?: string;
  };
  /** Select which version is active (by its key); the composer owns the selected-version state and, on select,
   *  loads that version's storyboard into the editor and plays its video. */
  onSelectVersion: (key: string) => void;
}

/**
 * The pan/zoom node canvas. Nodes/edges are DERIVED from the draft (the single source of truth);
 * dragging is preserved by feeding the previous nodes back into {@link deriveGraph}. Editing happens
 * inline inside the nodes via the {@link CanvasActionsContext}; connections and edge/keyboard deletion
 * are disabled in this slice (the graph is a view over a linear draft, not user-rewireable). While a
 * run is in flight, `generatedFrames`/`stage`/`running` choreograph the graph: generated frames appear
 * on their nodes, and the current node plus the connectors into it light up.
 */
export const StoryboardCanvas = ({
  draft,
  keyArt,
  brandLogoUrl,
  brandDomain,
  onChange,
  generatedFrames,
  stage,
  running,
  segments,
  staleFromBeat,
  compose,
  videoDurations,
  videoAspects,
  failedBeat,
  beatError,
  immersed,
  floating,
  footer,
  promoPanel,
  finishedAd,
  onSelectVersion,
  onRegenerateFrom,
  renderBlocked = false,
  onViewBeat,
}: StoryboardCanvasProps): React.ReactElement => {
  const opts = React.useMemo(
    () => ({
      generatedFrames,
      stage,
      running,
      segments,
      staleFromBeat,
      brandLogoUrl,
      brandDomain,
      failedBeat,
      beatError,
      videoAspects,
      withControls: floating,
      compose,
      finishedAd,
    }),
    [
      generatedFrames,
      stage,
      running,
      segments,
      staleFromBeat,
      brandLogoUrl,
      brandDomain,
      failedBeat,
      beatError,
      videoAspects,
      floating,
      compose,
      finishedAd,
    ],
  );
  const [nodes, setNodes, onNodesChange] = useNodesState<StoryboardNode>(deriveGraph(draft, keyArt, [], opts).nodes);

  // Re-derive nodes when the draft OR the live run changes, keeping each node's transient React Flow
  // state (selection drives the toolbar, measured is the cached size) by id via `prev`. The run inputs
  // are in the deps so generated frames and the active-node highlight update on every poll tick.
  // NOTE: node data reaches a custom node a render LATE (React Flow syncs the `nodes` prop into its
  // internal store via its own effect), so editable inputs inside a node must NOT render their value
  // straight from node data — they keep local state (see BeatNode's Motion field) or the caret resets.
  React.useEffect(() => {
    setNodes((prev) => deriveGraph(draft, keyArt, prev, opts).nodes);
  }, [draft, keyArt, opts, setNodes]);

  const edges: Edge[] = React.useMemo(() => deriveGraph(draft, keyArt, [], opts).edges, [draft, keyArt, opts]);

  const actions = React.useMemo<CanvasActions>(
    () => ({
      updateBeat: (i, patch) => onChange(updateBeat(draft, i, patch)),
      removeBeat: (i) => onChange(removeBeat(draft, i)),
      moveBeat: (i, dir) => onChange(moveBeat(draft, i, dir)),
      setAspect: (aspect) => onChange({ ...draft, aspect }),
      setCta: (cta) => onChange({ ...draft, cta }),
      regenerateFrom: onRegenerateFrom,
      renderBlocked,
      viewBeat: onViewBeat,
      videoDurations,
      selectVersion: onSelectVersion,
    }),
    [draft, onChange, onRegenerateFrom, renderBlocked, onViewBeat, videoDurations, onSelectVersion],
  );

  // Immersive: the on-canvas control card sits at x=-380, so the graph's bounds start left of F0 (a wider
  // graph fitView lays out left-to-right). Clear the top bar and the slim bottom bar; a mild left bias keeps
  // the control card off the very edge. Mobile uses a plain symmetric fit.
  const fitOptions = React.useMemo<FitViewOptions<StoryboardNode>>(
    () =>
      floating
        ? { padding: { top: '120px', right: '9%', bottom: '132px', left: '4%' } }
        : { padding: 0.15 },
    [floating],
  );

  // Re-frame the board the moment the beat cards first appear: on "Generate storyboard" (0 -> N beats) or a
  // reopened ad hydrating. An ANIMATED fitView so the new cards glide into frame instead of landing
  // off-screen (they insert between the key visual and the finished-ad node, widening the graph). Only on
  // 0 -> N, never on a later add/remove edit (the owner may have panned deliberately). Deferred a tick so the
  // freshly-derived nodes have measured before the fit is computed.
  const rf = React.useRef<ReactFlowInstance<StoryboardNode, Edge> | null>(null);
  const beatCount = draft.beats.length;
  const prevBeatCount = React.useRef(beatCount);
  React.useEffect(() => {
    const appeared = prevBeatCount.current === 0 && beatCount > 0;
    prevBeatCount.current = beatCount;
    if (!appeared) return undefined;
    const handle = window.setTimeout(() => {
      rf.current?.fitView({ ...fitOptions, duration: STORYBOARD_COMPOSE.FIT_MS });
    }, 80);
    return () => window.clearTimeout(handle);
  }, [beatCount, fitOptions]);

  // Multi-promo: adding/removing a promotion changes the graph shape (each extra promotion is its own
  // key-visual card, laid out above its run-start beat), so re-fit to bring the new visuals into frame once
  // the storyboard is composed. Skipped in the setup phase (no beats) and on first mount (no change yet).
  const promoCount = draft.promotions.length;
  const prevPromoCount = React.useRef(promoCount);
  React.useEffect(() => {
    const changed = prevPromoCount.current !== promoCount;
    prevPromoCount.current = promoCount;
    if (!changed || beatCount === 0) return undefined;
    const handle = window.setTimeout(() => {
      rf.current?.fitView({ ...fitOptions, duration: STORYBOARD_COMPOSE.FIT_MS });
    }, 80);
    return () => window.clearTimeout(handle);
  }, [promoCount, beatCount, fitOptions]);

  return (
    <CanvasActionsContext.Provider value={actions}>
      <PromoPanelContext.Provider value={promoPanel}>
        <CanvasFrame $immersive={immersed}>
          {/* Registers the --sb-glow-angle animatable property once for the cards' travelling Lumio border. */}
          <CanvasGlowAngleProperty />
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onInit={(instance) => {
              rf.current = instance;
            }}
            onNodesChange={onNodesChange}
            nodesConnectable={false}
            // The cards are laid out for you (reorder is via the beat toolbar, not drag), so nodes are NOT
            // user-draggable. With node-drag off, a press on a card propagates to the pane instead of moving
            // the card, so you can PAN by dragging anywhere on the board (a card, its motion prose, the gaps);
            // a click still focuses a field or fires a button (d3-zoom only pans past a small drag threshold).
            nodesDraggable={false}
            edgesFocusable={false}
            deleteKeyCode={null}
            fitView
            fitViewOptions={fitOptions}
            minZoom={0.4}
            maxZoom={1.5}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={24} />
            <Controls showInteractive={false} />
            {/* Immersive (Miro-style): a slim bottom pill (template - model - generate + status) floats over the
              board; the promotion / prompt / regenerate controls live in the on-canvas `promo` node (tied to
              the key visual by a connector), not a floating panel. nowheel/nopan keeps a pill's own scroll
              from panning the board. */}
            {floating && footer ? (
              <Panel position="bottom-center">
                <FooterPill className="nowheel nopan">{footer}</FooterPill>
              </Panel>
            ) : null}
          </ReactFlow>
          {/* No full-canvas "Composing…" overlay: the storyboard is written IN CONTEXT. While the model runs,
            the beat cards render as shimmer skeletons (deriveGraph `compose`), then each beat's motion prose
            types in, with the connectors into the worked beat animating (see BeatNode + graph). */}
        </CanvasFrame>
      </PromoPanelContext.Provider>
    </CanvasActionsContext.Provider>
  );
};

export default StoryboardCanvas;
